import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Lock, CreditCard, Check, ArrowUpRight, ChevronLeft } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { buildWhatsappUrl, CLINIC_WHATSAPP } from "@/lib/clinic";

const SERVICES = [
  { name: "OPD Consultation", fee: 100 },
  { name: "Root Canal Treatment (RCT)", fee: 2500 },
  { name: "Single-Sitting RCT", fee: 3000 },
  { name: "Re-RCT", fee: 3500 },
  { name: "Cap PFM [Vita]", fee: 1500 },
  { name: "Cap PFM [Ivoclar]", fee: 2500 },
  { name: "Cap Zirconia", fee: 7000 },
  { name: "Tooth-Colored Filling", fee: 700 },
  { name: "Ultrasonic Scaling", fee: 700 },
  { name: "Polishing", fee: 500 },
  { name: "Gutka Cleaning", fee: 1200 },
  { name: "Treatment of Bleeding Gums", fee: 800 },
  { name: "Flap Surgery", fee: 4000 },
  { name: "Cyst Removal", fee: 5000 },
  { name: "Dental Bleaching", fee: 1000 },
  { name: "Denture / Implant Denture", fee: 5000 },
  { name: "Orthodontic Treatment (Braces)", fee: 15000 },
  { name: "Digital X-Ray", fee: 200 },
];

const CONSULTATION_FEE = 100;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30",
];

export default function BookAppointment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [service, setService] = useState("");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [rzpLoading, setRzpLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // saved appointment for WA button

  // Preload Razorpay Checkout script
  useEffect(() => {
    if (document.getElementById("razorpay-checkout-script")) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.id = "razorpay-checkout-script";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const formattedDate = useMemo(() => date ? date.toISOString().slice(0, 10) : "", [date]);
  const selectedService = useMemo(() => SERVICES.find((s) => s.name === service) || null, [service]);
  const serviceFee = selectedService?.fee ?? 0;
  const isOpd = service === "OPD Consultation";
  const consultationFee = isOpd ? 0 : CONSULTATION_FEE;
  const totalAmount = consultationFee + serviceFee;

  const canProceed1 = !!date && !!time && !!service;
  const detailsValid = details.name.trim() && details.phone.trim();

  const showValidationToast = () => {
    if (!details.name.trim()) return toast.error("Please enter your full name");
    if (!details.phone.trim()) return toast.error("Please enter your phone number");
  };

  const handleBookAndPay = async () => {
    if (!detailsValid) { showValidationToast(); return; }
    setLoading(true);
    try {
      const apptRes = await api.post("/appointments", {
        ...details,
        service,
        service_fee: serviceFee,
        appointment_date: formattedDate,
        appointment_time: time,
      });
      const appointment_id = apptRes.data.id;
      // Persist appointment id so /payment-success can send WhatsApp confirmation
      try {
        localStorage.setItem("last_appointment_id", appointment_id);
      } catch (storageErr) {
        console.warn("Could not persist appointment id:", storageErr);
      }
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { appointment_id, origin_url });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const handleBookAndPayRazorpay = async () => {
    if (!detailsValid) { showValidationToast(); return; }
    if (!window.Razorpay) {
      toast.error("Payment SDK loading, please try again in a moment.");
      return;
    }
    setRzpLoading(true);
    try {
      const apptRes = await api.post("/appointments", {
        ...details,
        service,
        service_fee: serviceFee,
        appointment_date: formattedDate,
        appointment_time: time,
      });
      const appointment_id = apptRes.data.id;

      const orderRes = await api.post("/payments/razorpay/create-order", { appointment_id });
      const order = orderRes.data;

      try { localStorage.setItem("last_appointment_id", appointment_id); } catch (_) {}

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "The Dental Clinic by Dr. Ashish",
        description: `${service} — Appointment on ${formattedDate}`,
        order_id: order.order_id,
        prefill: {
          name: order.prefill?.name || details.name,
          email: order.prefill?.email || details.email,
          contact: order.prefill?.contact || details.phone,
        },
        notes: { appointment_id, service },
        theme: { color: "#0f766e" },
        modal: {
          ondismiss: () => {
            setRzpLoading(false);
            toast.info("Payment cancelled. You can retry anytime.");
          },
        },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              appointment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful!");
            navigate(`/payment-success?rzp=1&appointment_id=${appointment_id}`);
          } catch (err) {
            toast.error(formatError(err) || "Payment verification failed. Please contact clinic.");
            setRzpLoading(false);
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setRzpLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatError(err));
      setRzpLoading(false);
    }
  };

  const handleBookWithoutPay = async () => {
    if (!detailsValid) { showValidationToast(); return; }
    setLoading(true);
    try {
      const res = await api.post("/appointments", {
        ...details,
        service,
        service_fee: serviceFee,
        appointment_date: formattedDate,
        appointment_time: time,
      });
      const appt = res.data;
      setConfirmed(appt);
      // Auto-open WhatsApp with booking confirmation (works because triggered by user click)
      try {
        window.open(buildWhatsappUrl(appt), "_blank", "noopener");
      } catch (popupErr) {
        console.warn("Could not open WhatsApp window:", popupErr);
      }
      toast.success("Appointment requested. Confirm on WhatsApp.");
      setStep(3);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-clinic-bg text-clinic-ink pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Link to="/" data-testid="book-back-link" className="inline-flex items-center gap-1 text-sm text-clinic-muted hover:text-clinic-pine mb-6">
          <ChevronLeft size={16} /> Back to home
        </Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-clinic-muted">Booking</div>
            <h1 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">Reserve your visit.</h1>
            <p className="text-clinic-muted mt-3 max-w-lg">Three calm steps. SSL-encrypted, Razorpay & Stripe secured payment. WhatsApp option available.</p>
          </div>
          <Stepper step={step} />
        </div>

        <div className="card-elevated p-6 md:p-10">
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h3 className="font-heading text-xl tracking-tight mb-4">Pick a date</h3>
                <div className="rounded-2xl border border-clinic-line p-3 bg-white">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    data-testid="appointment-calendar"
                  />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl tracking-tight mb-4">Choose a service</h3>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="service-select" className="rounded-xl border-clinic-line py-6">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.name} value={s.name} data-testid={`service-option-${s.name.split(" ")[0].toLowerCase()}`}>
                        <span className="flex items-center justify-between gap-4 w-full">
                          <span>{s.name}</span>
                          <span className="text-xs text-clinic-pine font-medium">{s.fee > 0 ? `₹${s.fee.toLocaleString("en-IN")}` : "—"}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <h3 className="font-heading text-xl tracking-tight mt-8 mb-4">Pick a time</h3>
                <div className="grid grid-cols-3 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      data-testid={`slot-${t}`}
                      onClick={() => setTime(t)}
                      className={`px-3 py-2.5 rounded-full text-sm border transition ${
                        time === t
                          ? "bg-clinic-pine text-clinic-bg border-clinic-pine"
                          : "border-clinic-line hover:border-clinic-pine"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                <button
                  data-testid="step1-next"
                  disabled={!canProceed1}
                  onClick={() => setStep(2)}
                  className="btn-primary mt-10 w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowUpRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="font-heading text-2xl tracking-tight">Your details</h3>
              <p className="text-clinic-muted text-sm mt-1">We use this only to confirm your appointment.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <input data-testid="detail-name" required value={details.name} onChange={(e)=>setDetails({...details, name:e.target.value})} placeholder="Full name *" className="px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
                <input data-testid="detail-email" type="email" value={details.email} onChange={(e)=>setDetails({...details, email:e.target.value})} placeholder="Email (optional)" className="px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
                <input data-testid="detail-phone" required value={details.phone} onChange={(e)=>setDetails({...details, phone:e.target.value})} placeholder="Phone (WhatsApp preferred) *" className="md:col-span-2 px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
                <textarea data-testid="detail-notes" value={details.notes} onChange={(e)=>setDetails({...details, notes:e.target.value})} placeholder="Anything we should know? (optional — allergies, anxiety, prior treatments...)" rows={4} className="md:col-span-2 px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine resize-none" />
              </div>

              <div className="mt-8 p-6 rounded-2xl bg-clinic-sage/40 border border-clinic-line" data-testid="price-breakup">
                <div className="flex items-start justify-between gap-3 pb-4 border-b border-clinic-line/80">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-clinic-muted">Appointment</div>
                    <div className="font-heading text-lg mt-1 leading-tight">{service}</div>
                    <div className="text-sm text-clinic-muted mt-1">{formattedDate} · {time}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-[0.18em] text-clinic-muted">Total payable</div>
                    <div className="font-heading text-3xl text-clinic-pine mt-1" data-testid="total-amount">₹{totalAmount.toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  {!isOpd && (
                    <div className="flex items-center justify-between">
                      <span className="text-clinic-ink/85">Consultation fee</span>
                      <span className="font-medium" data-testid="consultation-fee">₹{CONSULTATION_FEE.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-clinic-ink/85">{service || "Selected service"}</span>
                    <span className="font-medium" data-testid="service-fee">
                      {serviceFee > 0 ? `₹${serviceFee.toLocaleString("en-IN")}` : "Included"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 mt-2 border-t border-clinic-line/80">
                    <span className="font-heading text-clinic-ink">Grand total</span>
                    <span className="font-heading text-clinic-pine text-lg">₹{totalAmount.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <p className="text-[11px] text-clinic-muted mt-3">
                  Final treatment costs (additional sittings, materials chosen) will be discussed during consultation. Digital X-Ray ₹200 charged separately only if required.
                </p>
              </div>

              <div className="mt-6 flex items-center gap-4 text-xs text-clinic-muted">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-clinic-pine" /> SSL</span>
                <span className="inline-flex items-center gap-1.5"><Lock size={14} className="text-clinic-pine" /> Razorpay / Stripe</span>
                <span className="inline-flex items-center gap-1.5"><CreditCard size={14} className="text-clinic-pine" /> UPI · Card · Netbanking</span>
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                <button data-testid="pay-razorpay-btn" disabled={rzpLoading || loading} onClick={handleBookAndPayRazorpay} className="btn-primary justify-center disabled:opacity-60 disabled:cursor-wait">
                  {rzpLoading ? "Opening…" : "Pay with UPI/Card (Razorpay)"} <ArrowUpRight size={18} />
                </button>
                <button data-testid="pay-now-btn" disabled={loading || rzpLoading} onClick={handleBookAndPay} className="btn-ghost justify-center disabled:opacity-60 disabled:cursor-wait">
                  {loading ? "Redirecting..." : "Pay with Card (Stripe)"} <ArrowUpRight size={18} />
                </button>
                <button data-testid="pay-at-clinic-btn" disabled={loading || rzpLoading} onClick={handleBookWithoutPay} className="btn-ghost justify-center disabled:opacity-60 disabled:cursor-wait">
                  {loading ? "Saving..." : "Pay at clinic"}
                </button>
              </div>
              <a href={`https://wa.me/${CLINIC_WHATSAPP}?text=${encodeURIComponent(`Hi, I'd like to book ${service} on ${formattedDate} at ${time}. My name is ${details.name}.`)}`} target="_blank" rel="noreferrer" data-testid="whatsapp-book-btn" className="mt-4 inline-flex items-center gap-2 text-sm text-[#25D366] hover:underline">
                <FaWhatsapp /> Or book via WhatsApp
              </a>

              <button onClick={() => setStep(1)} data-testid="step2-back" className="block mt-6 text-sm text-clinic-muted hover:text-clinic-pine">← Back</button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-success" className="text-center py-10">
              <div className="w-16 h-16 rounded-full bg-clinic-sage/60 grid place-items-center mx-auto text-clinic-pine">
                <Check size={32} />
              </div>
              <h3 className="font-heading text-3xl mt-6 tracking-tight">Appointment requested</h3>
              <p className="text-clinic-muted mt-3 max-w-md mx-auto">
                We've received your request for <strong>{service}</strong> on <strong>{formattedDate} at {time}</strong>.
                Tap the button below to confirm us via WhatsApp.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {confirmed ? (
                  <a
                    data-testid="confirm-whatsapp-btn"
                    href={buildWhatsappUrl(confirmed)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB854] text-white font-semibold rounded-[14px] px-7 py-4 transition shadow-lg shadow-[#25D366]/30"
                  >
                    <FaWhatsapp size={20} /> Send confirmation on WhatsApp
                  </a>
                ) : (
                  <a href={`https://wa.me/${CLINIC_WHATSAPP}`} target="_blank" rel="noreferrer" className="btn-ghost"><FaWhatsapp /> Open WhatsApp</a>
                )}
                <Link to="/" data-testid="back-home-btn" className="btn-ghost">Back to home</Link>
              </div>
              <p className="text-xs text-clinic-muted mt-4">A WhatsApp window should have opened automatically — if not, tap the button above.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const items = ["Schedule", "Details", "Confirm"];
  return (
    <div className="flex items-center gap-2 text-xs">
      {items.map((it, i) => (
        <div key={it} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-medium ${
            step > i ? "bg-clinic-pine text-clinic-bg" : step === i + 1 ? "bg-clinic-honey text-clinic-ink" : "bg-clinic-line text-clinic-muted"
          }`}>
            {i + 1}
          </div>
          <span className={`uppercase tracking-[0.18em] ${step === i + 1 ? "text-clinic-ink" : "text-clinic-muted"}`}>{it}</span>
          {i < items.length - 1 && <div className="w-6 h-px bg-clinic-line" />}
        </div>
      ))}
    </div>
  );
}
